import { useState } from 'react'
import { Link } from 'react-router-dom'
import { joinWaitlist } from '@/api/auth'
import { COUNTRIES, DEFAULT_COUNTRY } from '@/data/countries'
import toast from 'react-hot-toast'

export default function WaitlistPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [organization, setOrganization] = useState('')
  const [applicationIdea, setApplicationIdea] = useState('')
  const [country, setCountry] = useState(DEFAULT_COUNTRY)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !email) {
      toast.error('Please fill in name and email')
      return
    }
    if (!country) {
      toast.error('Please select your country')
      return
    }

    setLoading(true)
    try {
      await joinWaitlist({
        name,
        email,
        organization,
        application_idea: applicationIdea,
        country: country || undefined,
      })
      setSubmitted(true)
      toast.success('Successfully joined the waitlist!')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to join waitlist'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
        padding: '2rem',
      }}>
        <div style={{
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          animation: 'slideUp 0.3s ease-out',
        }}>
          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--color-accent-weak)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
          }}>
            <span className="material-icons" style={{ fontSize: 37, color: 'var(--color-accent)' }}>
              celebration
            </span>
          </div>
          <h2 style={{ fontSize: '1.5625rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            You're on the List!
          </h2>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '1.0625rem',
            lineHeight: 1.7,
            marginBottom: '0.5rem',
          }}>
            Thank you for your interest in Twasta.ai, <strong style={{ color: 'var(--color-text)' }}>{name}</strong>!
          </p>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '1rem',
            lineHeight: 1.7,
            marginBottom: '2rem',
          }}>
            We'll notify you at <strong style={{ color: 'var(--color-text)' }}>{email}</strong> when
            your access is ready. In the meantime, stay tuned for updates.
          </p>
          <Link to="/" className="btn btn-primary btn-lg">
            <span className="material-icons" style={{ fontSize: 19 }}>arrow_back</span>
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--color-bg)',
      padding: '2rem',
    }}>
      <div style={{
        maxWidth: 480,
        width: '100%',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.8125rem',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0061D0, #009974)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            twasta.ai
          </span>
          <h2 style={{
            fontSize: '1.3125rem',
            fontWeight: 600,
            marginTop: '0.75rem',
          }}>
            Join the Waitlist
          </h2>
          <p style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text-secondary)',
            marginTop: '0.375rem',
          }}>
            Be among the first to experience AI-powered application development.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.125rem',
          }}
        >
          <div>
            <label htmlFor="wl-name">Full Name *</label>
            <input
              id="wl-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label htmlFor="wl-email">Email Address *</label>
            <input
              id="wl-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>

          <div>
            <label htmlFor="wl-org">Organization</label>
            <input
              id="wl-org"
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="Your company or team (optional)"
            />
          </div>

          {/* Country — the waitlist is the only intake while self-service
              sign-up is closed, so this is where a granted account's billing
              currency (IN -> INR, else USD) and tax rule are decided. */}
          <div>
            <label htmlFor="wl-country">Country *</label>
            <select
              id="wl-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              style={{ width: '100%' }}
            >
              <option value="">Select your country</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
              Used to determine your billing currency.
            </div>
          </div>

          <div>
            <label htmlFor="wl-idea">Application Idea</label>
            <textarea
              id="wl-idea"
              value={applicationIdea}
              onChange={(e) => setApplicationIdea(e.target.value)}
              placeholder="Briefly describe what kind of application you'd like to build with Twasta.ai..."
              rows={4}
              style={{ resize: 'vertical' }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg w-full"
            disabled={loading}
            style={{ marginTop: '0.25rem' }}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                Submitting...
              </>
            ) : (
              <>
                <span className="material-icons" style={{ fontSize: 19 }}>rocket_launch</span>
                Join Waitlist
              </>
            )}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '1.25rem',
          fontSize: '0.9375rem',
          color: 'var(--color-text-secondary)',
        }}>
          Already have an account?{' '}
          <Link to="/">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
